<template>
  <div
    v-if="company && !$apollo.queries.company.loading"
    id="company-details"
  >
    <div class="flex items-center justify-between px-10 py-8 bg-gray-100 border-b rounded-t-lg">
      <h1 class="text-xl font-semibold tracking-wider text-gray-800 uppercase">
        {{ $t('pages.companies.detailTitle') }}
      </h1>
      <button
        v-if="Object.keys(pendingChangesForRegeneration).length > 0"
        class="p-2 px-6 font-semibold text-yellow-600 uppercase bg-yellow-300 rounded hover:bg-yellow-400 hover:text-yellow-800"
        @click="showCompanyMutations()"
      >
        {{ $t('pages.companies.showPendingChanges') }}
      </button>
      <div class="flex justify-between">
        <Feature :feature-slug="featureNames.SUB_COMPANY_CREATOR">
          <div
            class="flex items-center mr-5 font-semibold text-gray-600 uppercase cursor-pointer hover:text-gray-800"
            @click="handleCreateChildClick"
          >
            {{ $t('pages.companies.createSubCompany') }}
          </div>
        </Feature>
        <router-link
          :to="`/companies`"
          class="flex items-center font-semibold text-gray-600 uppercase hover:text-gray-800"
        >
          {{ $t('common.backToList') }}
        </router-link>
      </div>
    </div>
    <company-header :company="company" />

    <tabs>
      <div class="p-10 pt-0">
        <tab
          id="config"
          :name="$t('pages.companies.tabs.config')"
        >
          <base-data :company="company" />

          <multi-tenant-status :company="company" />

          <Feature
            v-if="hasRole(ROLES.SUPER_ADMIN)"
            :feature-slug="featureNames.HOPPERMATION"
          >
            <email-verification :company="company" />
          </Feature>
          <images
            :images="company.images"
            :company-id="companyId"
          />
          <Feature
            v-if="hasRole(ROLES.SUPER_ADMIN)"
            v-slot="{feature}"
            :feature-slug="featureNames.VALUATION"
          >
            <phone-verification
              v-if="feature.config.isPhoneNumberVerificationEnabled
                && feature.config.canChangePhoneNumberVerification
                && companySettings"
              :company-id="companyId"
              :settings="companySettings"
              :refetch-settings="refetchCompanySettings"
            />
          </Feature>
          <theme
            :company-id="companyId"
            :theme="company.theme"
          />
          <ambassador
            :ambassador="company.ambassador"
            :company-id="companyId"
          />
          <deployment
            v-if="hasRole(ROLES.SUPER_ADMIN)"
            :site="company.site"
            :company-id="companyId"
          />
        </tab>

        <tab
          id="landingpages"
          :name="$t('pages.companies.tabs.landingpages')"
        >
          <landingpages
            :company="company"
            @refetch-company="refetchCompany"
          />
        </tab>

        <tab
          id="agents"
          :name="$t('pages.companies.tabs.agents')"
        >
          <Feature :feature-slug="featureNames.BACKOFFICE_LEADS">
            <lead-list :company-id="companyId" />
          </Feature>
          <users
            :company-id="companyId"
            :sso-providers="company.ssoProviders"
          />
        </tab>
        <tab
          v-if="isFeatureActive(featureNames.CONTRACT) || isFeatureActive(featureNames.BILLWERK)"
          id="contract"
          :name="$t('pages.companies.tabs.contract')"
        >
          <Feature
            :feature-slug="featureNames.BILLWERK"
            use-is-enabled
          >
            <billing-provider
              :company="company"
            />
          </Feature>
          <Feature
            v-if="!isExpertCompany()"
            :feature-slug="featureNames.BILLWERK"
            use-is-enabled
          >
            <section class="w-full">
              <h1 class="flex items-center justify-between">
                {{ $t('pages.companies.tabs.manualProductChange') }}
              </h1>
              <div class="flex gap-4">
                <manual-upgrade
                  :company-id="companyId"
                  :product-type="company.productType"
                />
                <manual-downgrade
                  :company-id="companyId"
                  :product-type="company.productType"
                />
              </div>
            </section>
          </Feature>
          <billing
            :company-id="companyId"
          />
          <contracts
            :company-id="companyId"
          />
        </tab>
        <tab
          v-if="hasRole(ROLES.SUPER_ADMIN)"
          id="contact-persons"
          :name="$t('pages.companies.tabs.contactPersons')"
        >
          <contact-persons :company-id="companyId" />
        </tab>
        <tab
          v-if="hasRole(ROLES.SUPER_ADMIN)"
          id="settings"
          :name="$t('pages.companies.tabs.settings')"
        >
          <settings :company-id="companyId" />
        </tab>
        <tab
          v-if="isFeatureActive(featureNames.PERFORMANCE_MARKETING)"
          id="performance"
          :name="$t('pages.companies.tabs.performance')"
        >
          <performance :company-id="companyId" />
        </tab>
        <tab
          v-if="isFeatureActive(featureNames.DASHBOARD_PRESENTATION)"
          id="customer-status"
          :name="$t('pages.companies.tabs.customerStatus')"
        >
          <customers-status
            :company-id="companyId"
            :platform="company?.platform"
          />
        </tab>
        <tab
          v-if="isFeatureActive(featureNames.STATISTIC_DASHBOARD)"
          id="success-analysis"
          :name="$t('pages.companies.tabs.successAnalysis')"
        >
          <StatisticDashboard :company-id="companyId" />
        </tab>
        <tab
          v-if="isFeatureActive(featureNames.CONNECTION_MANAGER)"
          :name="$t('components.company.connections.title')"
        >
          <Connections
            :company-id="companyId"
            :platform="company?.platform"
          />
        </tab>
        <tab
          v-if="hasRole(ROLES.ACTIVITY_VIEWER)"
          id="activity"
          name="Activity"
        >
          <div class="flex items-center gap-3 mt-6 mb-4 ml-2 flex-wrap">
            <div class="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
              <button
                class="px-4 py-1.5 font-medium"
                :class="companyMainTab === 'platform' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'"
                @click="companyMainTab = 'platform'"
              >
                Platform Activity
              </button>
              <button
                class="px-4 py-1.5 font-medium border-l border-gray-300"
                :class="companyMainTab === 'leads' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'"
                @click="companyMainTab = 'leads'"
              >
                Lead Activity
              </button>
            </div>
            <div
              v-if="companyMainTab === 'platform'"
              class="flex rounded-lg border border-gray-300 overflow-hidden text-sm"
            >
              <button
                class="px-4 py-1.5 font-medium"
                :class="companyActivityTab === 'history' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'"
                @click="companyActivityTab = 'history'"
              >
                History
              </button>
              <button
                class="px-4 py-1.5 font-medium border-l border-gray-300"
                :class="companyActivityTab === 'scheduled' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'"
                @click="companyActivityTab = 'scheduled'"
              >
                Scheduled
              </button>
            </div>
            <select
              v-if="companyMainTab === 'platform'"
              v-model="companyActivityType"
              class="text-sm border border-gray-300 rounded px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option :value="null">
                All types
              </option>
              <option value="POST">
                Post
              </option>
              <option value="INVITATION">
                Invitation
              </option>
              <option value="MARKETING_TOPIC">
                Topic
              </option>
            </select>
            <select
              v-else
              v-model="companyLeadFilterType"
              class="text-sm border border-gray-300 rounded px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option :value="null">
                All types
              </option>
              <option value="NEW_LEAD">
                New Lead
              </option>
              <option value="CONVERSION">
                Conversion
              </option>
            </select>
            <div class="w-px h-6 bg-gray-300" />
            <select
              v-model.number="companyPollIntervalMin"
              class="text-sm border border-gray-300 rounded px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option :value="1">
                1 min
              </option>
              <option :value="2">
                2 min
              </option>
              <option :value="5">
                5 min
              </option>
              <option :value="10">
                10 min
              </option>
              <option :value="15">
                15 min
              </option>
              <option :value="30">
                30 min
              </option>
              <option :value="60">
                60 min
              </option>
            </select>
            <button
              class="px-3 py-1.5 text-sm font-medium text-indigo-600 border border-indigo-300 rounded hover:bg-indigo-50"
              @click="companyMainTab === 'platform' ? $refs.companyActivityFeed.refresh() : $refs.companyLeadFeed.refresh()"
            >
              ↺ Refresh
            </button>
          </div>
          <ActivityFeed
            v-if="companyMainTab === 'platform'"
            ref="companyActivityFeed"
            :filter-company-id="companyId"
            :filter-type="companyActivityType"
            :scheduled="companyActivityTab === 'scheduled'"
            :poll-interval-min="companyPollIntervalMin"
          />
          <LeadActivityFeed
            v-else
            ref="companyLeadFeed"
            :filter-company-id="companyId"
            :filter-type="companyLeadFilterType"
            :poll-interval-min="companyPollIntervalMin"
          />
        </tab>
      </div>
    </tabs>
  </div>
</template>

<script>

import PhoneVerification from '@/components/company/phoneVerification.vue'
import BaseData from '@/components/company/base-data'
import Theme from '@/components/company/theme'
import LeadList from '@/components/leads/list'
import Ambassador from '@/components/company/ambassador'
import Users from '@/components/company/users'
import Deployment from '@/components/company/deployment'
import Settings from '@/components/company/settings'
import Landingpages from '@/components/landingpages'
import Images from '@/components/company/images'
import Tabs from '@/components/navigation/tabs'
import Tab from '@/components/navigation/tab'
import Contracts from '@/components/company/contract/contracts'
import Billing from '@/components/company/billing'
import BillingProvider from '@/components/company/billing-provider'
import ManualUpgrade from '@/components/company/manual-upgrade'
import ManualDowngrade from '@/components/company/manual-downgrade'
import ContactPersons from '@/components/company/contact-persons.vue'
import CompanyHeader from '@/components/company/header'
import Performance from '@/components/company/performance.vue'
import CustomersStatus from '@/components/company/customers-status.vue'
import StatisticDashboard from '@/components/company/statistic-dashboard.vue'
import CompanyMutations from '@/components/modals/company/CompanyMutations.vue'
import GET_PENDING_CHANGES_FOR_REGENERATION from '@/graphql/GetPendingChangesForRegeneration.gql'
import GET_COMPANY from '@/graphql/GetCompany.gql'
import GET_COMPANY_SETTINGS from '@/graphql/GetCompanySettings.gql'
import featureMixin from '@/mixins/feature'
import role from '@/mixins/role'
import EmailVerification from '@/components/company/emailVerification.vue'
import MultiTenantStatus from '@/components/company/multi-tenant-status.vue'
import CompanyService from '@/pages/companies/services/CompanyService'
import Connections from '@/components/company/connections/connections.vue'
import ActivityFeed from '@/components/post-history/ActivityFeed.vue'
import LeadActivityFeed from '@/components/lead-activity/LeadActivityFeed.vue'

export default {
  components: {
    BaseData,
    Theme,
    LeadList,
    Landingpages,
    Ambassador,
    Users,
    Deployment,
    Settings,
    Images,
    PhoneVerification,
    Tabs,
    Tab,
    Contracts,
    Billing,
    BillingProvider,
    ManualUpgrade,
    ManualDowngrade,
    ContactPersons,
    CompanyHeader,
    Performance,
    CustomersStatus,
    StatisticDashboard,
    EmailVerification,
    MultiTenantStatus,
    Connections,
    ActivityFeed,
    LeadActivityFeed
  },
  mixins: [featureMixin, role],
  data () {
    return {
      phoneVerificationEnabled: false,
      pendingChangesForRegeneration: {},
      companyMainTab: 'platform',
      companyActivityTab: 'history',
      companyActivityType: null,
      companyLeadFilterType: null,
      companyPollIntervalMin: 5
    }
  },
  computed: {
    companyId () {
      return this.$route.params.id
    }
  },
  methods: {
    handleCreateChildClick () {
      CompanyService.setCompany(this.company)
      this.$router.push(`/companies/create-child/${this.companyId}`)
    },
    showCompanyMutations () {
      this.$modal.show(
        CompanyMutations,
        { companyId: this.company.id },
        { width: 800 }
      )
    },
    async refetchCompanySettings () {
      await this.$apollo.queries.companySettings.refetch()
    },
    /**
     * Re-read the company after a lifecycle transition.
     *
     * Activation changes the company's landing pages, and this query is what
     * supplies them to the Landing Pages tab, so without it the tab kept
     * rendering the pre-activation list.
     */
    async refetchCompany () {
      await this.$apollo.queries.company.refetch()
    },
    isExpertCompany () {
      return this.company.productType === 'EXPERT'
    }
  },
  apollo: {
    company: {
      query: GET_COMPANY,
      variables () {
        return {
          id: this.companyId
        }
      },
      fetchPolicy: 'network-only'
    },
    companySettings: {
      query: GET_COMPANY_SETTINGS,
      variables () {
        return {
          id: this.companyId
        }
      }
    },
    pendingChangesForRegeneration: {
      query: GET_PENDING_CHANGES_FOR_REGENERATION,
      variables () {
        return { companyId: this.companyId }
      }
    }
  }
}
</script>
